import { useState, useEffect, useCallback } from "react";
import { useApi } from "./useApi";
import { getInventoryDisplayColumns } from "../config/inventoryFields";

export const useInventoryData = (
  tenantId,
  initialFilters = {},
  grouped = false
) => {
  const { getInventoryData, getGroupedInventoryData, loading, error } =
    useApi();
  const [inventoryData, setInventoryData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [grandTotalMRC, setGrandTotalMRC] = useState(0);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState({
    provider: [],
    category: [],
    status: [],
    serviceType: [],
    billingAccount: [],
    search: "",
    siteRegion: [],
    siteClassification: [],
    siteGeneralManagerId: [],
    siteSdWanAppliance: [],
    siteStatus: [],
    circuitAction: [],
    contractEndsWithin: "",
    ...initialFilters,
  });
  const [sortConfig, setSortConfig] = useState({
    sortBy: null,
    sortOrder: null,
  });
  const [hasAuthError, setHasAuthError] = useState(false);

  const loadInventoryData = useCallback(
    async (
      page = 1,
      newFilters = {},
      pageSizeOverride = null,
      sortOverride = null
    ) => {
      try {
        const currentFilters = { ...filters, ...newFilters };
        const pageSizeToUse = pageSizeOverride || pagination.pageSize;

        // Use sortOverride if provided, otherwise use current sortConfig from closure
        // This ensures we always use the latest sort config when called from useEffect
        const activeSort = sortOverride || sortConfig;

        // Debug: Log sort configuration being used
        if (activeSort.sortBy) {
          console.log("📊 Loading data with sort:", {
            sortBy: activeSort.sortBy,
            sortOrder: activeSort.sortOrder,
            usingOverride: !!sortOverride,
          });
        }

        // Normalize filter values: handle arrays and convert legacy string values
        // Default behavior: show only "In Service" or "suspended" if no status filter is set
        const normalizedFilters = {
          provider: Array.isArray(currentFilters.provider)
            ? currentFilters.provider
            : currentFilters.provider === "all" || !currentFilters.provider
            ? []
            : [currentFilters.provider],
          category: Array.isArray(currentFilters.category)
            ? currentFilters.category
            : currentFilters.category === "all" || !currentFilters.category
            ? []
            : [currentFilters.category],
          status: (() => {
            // Default behavior: show only "In Service" or "suspended" if no status filter is set
            if (Array.isArray(currentFilters.status)) {
              // If status array is empty, apply default filter
              return currentFilters.status.length === 0
                ? ["In Service", "suspended"]
                : currentFilters.status;
            }
            // Handle legacy string values
            if (currentFilters.status === "all" || !currentFilters.status) {
              return ["In Service", "suspended"]; // Default filter
            }
            return [currentFilters.status];
          })(),
          serviceType: Array.isArray(currentFilters.serviceType)
            ? currentFilters.serviceType
            : currentFilters.serviceType === "all" ||
              !currentFilters.serviceType
            ? []
            : [currentFilters.serviceType],
          billingAccount: Array.isArray(currentFilters.billingAccount)
            ? currentFilters.billingAccount
            : currentFilters.billingAccount === "all" ||
              !currentFilters.billingAccount
            ? []
            : [currentFilters.billingAccount],
          search: currentFilters.search || "",
          siteRegion: Array.isArray(currentFilters.siteRegion)
            ? currentFilters.siteRegion.filter(
                (v) => v != null && String(v).trim() !== ""
              )
            : [],
          siteClassification: Array.isArray(currentFilters.siteClassification)
            ? currentFilters.siteClassification.filter(
                (v) => v != null && String(v).trim() !== ""
              )
            : [],
          siteGeneralManagerId: Array.isArray(
            currentFilters.siteGeneralManagerId
          )
            ? currentFilters.siteGeneralManagerId.filter(
                (v) => v != null && String(v).trim() !== ""
              )
            : [],
          siteSdWanAppliance: Array.isArray(currentFilters.siteSdWanAppliance)
            ? currentFilters.siteSdWanAppliance.filter(
                (v) => v != null && String(v).trim() !== ""
              )
            : [],
          siteStatus: Array.isArray(currentFilters.siteStatus)
            ? currentFilters.siteStatus.filter(
                (v) => v != null && String(v).trim() !== ""
              )
            : [],
          circuitAction: Array.isArray(currentFilters.circuitAction)
            ? currentFilters.circuitAction.filter(
                (v) => v != null && String(v).trim() !== ""
              )
            : [],
          contractEndsWithin:
            currentFilters.contractEndsWithin != null &&
            String(currentFilters.contractEndsWithin).trim() !== ""
              ? String(currentFilters.contractEndsWithin).trim()
              : "",
        };

        // Use grouped endpoint if grouped mode is enabled
        const apiCall = grouped ? getGroupedInventoryData : getInventoryData;
        const response = await apiCall({
          page,
          pageSize: pageSizeToUse,
          tenant_id: tenantId,
          ...normalizedFilters,
          ...(activeSort.sortBy && {
            sortBy: activeSort.sortBy,
            sortOrder: activeSort.sortOrder || "asc",
          }),
        });

        if (response?.data) {
          if (grouped) {
            // Grouped mode: response.data is array of address groups
            const groups = Array.isArray(response.data) ? response.data : [];

            // Extract grand total MRC from response
            const totalMRC = response.grandTotalMRC || 0;
            setGrandTotalMRC(totalMRC);

            // Handle API response structure - pagination can be nested or at root
            const paginationData = response.pagination || response;
            const responsePageSize =
              paginationData.pageSize || response.pageSize || pageSizeToUse;
            const responsePage = paginationData.page || response.page || page;
            const responseTotal =
              paginationData.total || response.count || response.total || 0;
            const responseTotalRecords =
              paginationData.totalRecords || response.totalRecords || 0;
            const responseTotalPages =
              paginationData.totalPages ||
              Math.ceil(responseTotal / responsePageSize);

            const paginationInfo = {
              page: responsePage,
              total: responseTotal,
              siteCount:
                paginationData.siteCount != null
                  ? paginationData.siteCount
                  : responseTotal,
              totalRecords: responseTotalRecords,
              totalPages: responseTotalPages,
              pageSize: responsePageSize,
            };

            console.log("📊 Pagination Info (grouped):", paginationInfo);
            console.log("📦 Address groups loaded:", groups.length);
            console.log("📝 Total individual records:", responseTotalRecords);
            console.log("💰 Grand Total MRC:", totalMRC);

            // Set groups as inventory data (for grouped table component)
            setInventoryData(groups);
            setPagination((prev) => ({ ...prev, ...paginationInfo }));

            // Extract items from first group to determine columns
            if (
              groups.length > 0 &&
              groups[0].items &&
              groups[0].items.length > 0
            ) {
              const displayColumns = getInventoryDisplayColumns(
                groups[0].items
              );
              setColumns(displayColumns);
            } else {
              setColumns([]);
            }
          } else {
            // Non-grouped mode: response.data is array of individual records
            let records = Array.isArray(response.data) ? response.data : [];

            // Ensure all records have an id field - add debug logging
            records = records.map((record, index) => {
              if (!record.id && record.id !== 0) {
                console.warn(
                  `Record at index ${index} is missing id field:`,
                  record
                );
                console.warn("Available keys:", Object.keys(record));
              }
              // Verify address_id is present (for address standardization functionality)
              if (
                record.address_id === undefined &&
                record.address_id !== null
              ) {
                console.warn(
                  `Record at index ${index} is missing address_id field:`,
                  record
                );
              }
              return record;
            });

            // Debug: Log first record to see structure
            if (records.length > 0) {
              console.log("Sample inventory record:", records[0]);
              console.log("Record ID:", records[0]?.id);
              console.log("Record address_id:", records[0]?.address_id);
              console.log("Record keys:", Object.keys(records[0]));
            }

            // Handle API response structure - pagination can be nested or at root
            const paginationData = response.pagination || response;
            const responsePageSize =
              paginationData.pageSize || response.pageSize || pageSizeToUse;
            const responsePage = paginationData.page || response.page || page;
            const responseTotal =
              paginationData.total || response.count || response.total || 0;
            const responseTotalPages =
              paginationData.totalPages ||
              Math.ceil(responseTotal / responsePageSize);

            const paginationInfo = {
              page: responsePage,
              total: responseTotal,
              totalPages: responseTotalPages,
              pageSize: responsePageSize,
            };

            console.log("📊 Pagination Info:", paginationInfo);
            console.log("📦 Records loaded:", records.length);

            setInventoryData(records);
            setPagination((prev) => ({ ...prev, ...paginationInfo }));

            // Set display columns using inventory field constants
            if (records.length > 0) {
              const displayColumns = getInventoryDisplayColumns(records);
              setColumns(displayColumns);
            } else {
              setColumns([]);
            }
          }
        } else {
          setInventoryData([]);
          setColumns([]);
        }
      } catch (err) {
        console.error("Failed to load inventory data:", err);
        
        // Check if it's an authentication error
        if (err.status === 401 || err.isAuthError) {
          console.log("Authentication error detected - stopping requests");
          setHasAuthError(true);
        }
      }
    },
    [
      getInventoryData,
      getGroupedInventoryData,
      grouped,
      filters,
      pagination.pageSize,
      tenantId,
      sortConfig,
    ]
  );

  const updateFilters = useCallback((newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const changePage = useCallback(
    (page) => {
      loadInventoryData(page);
    },
    [loadInventoryData]
  );

  const changePageSize = useCallback(
    async (pageSize) => {
      // Update pagination state first
      setPagination((prev) => ({ ...prev, pageSize, page: 1 }));

      // Reload data with new page size
      // Use a fresh call to loadInventoryData with the new page size
      const currentFilters = { ...filters };

      // Normalize filter values: handle arrays and convert legacy string values
      // Default behavior: show only "In Service" or "suspended" if no status filter is set
      const normalizedFilters = {
        provider: Array.isArray(currentFilters.provider)
          ? currentFilters.provider
          : currentFilters.provider === "all" || !currentFilters.provider
          ? []
          : [currentFilters.provider],
        category: Array.isArray(currentFilters.category)
          ? currentFilters.category
          : currentFilters.category === "all" || !currentFilters.category
          ? []
          : [currentFilters.category],
        status: (() => {
          // Default behavior: show only "In Service" or "suspended" if no status filter is set
          if (Array.isArray(currentFilters.status)) {
            // If status array is empty, apply default filter
            return currentFilters.status.length === 0
              ? ["In Service", "suspended"]
              : currentFilters.status;
          }
          // Handle legacy string values
          if (currentFilters.status === "all" || !currentFilters.status) {
            return ["In Service", "suspended"]; // Default filter
          }
          return [currentFilters.status];
        })(),
        serviceType: Array.isArray(currentFilters.serviceType)
          ? currentFilters.serviceType
          : currentFilters.serviceType === "all" || !currentFilters.serviceType
          ? []
          : [currentFilters.serviceType],
        billingAccount: Array.isArray(currentFilters.billingAccount)
          ? currentFilters.billingAccount
          : currentFilters.billingAccount === "all" ||
            !currentFilters.billingAccount
          ? []
          : [currentFilters.billingAccount],
        search: currentFilters.search || "",
        siteRegion: Array.isArray(currentFilters.siteRegion)
          ? currentFilters.siteRegion.filter(
              (v) => v != null && String(v).trim() !== ""
            )
          : [],
        siteClassification: Array.isArray(currentFilters.siteClassification)
          ? currentFilters.siteClassification.filter(
              (v) => v != null && String(v).trim() !== ""
            )
          : [],
        siteGeneralManagerId: Array.isArray(currentFilters.siteGeneralManagerId)
          ? currentFilters.siteGeneralManagerId.filter(
              (v) => v != null && String(v).trim() !== ""
            )
          : [],
        siteSdWanAppliance: Array.isArray(currentFilters.siteSdWanAppliance)
          ? currentFilters.siteSdWanAppliance.filter(
              (v) => v != null && String(v).trim() !== ""
            )
          : [],
        siteStatus: Array.isArray(currentFilters.siteStatus)
          ? currentFilters.siteStatus.filter(
              (v) => v != null && String(v).trim() !== ""
            )
          : [],
        circuitAction: Array.isArray(currentFilters.circuitAction)
          ? currentFilters.circuitAction.filter(
              (v) => v != null && String(v).trim() !== ""
            )
          : [],
        contractEndsWithin:
          currentFilters.contractEndsWithin != null &&
          String(currentFilters.contractEndsWithin).trim() !== ""
            ? String(currentFilters.contractEndsWithin).trim()
            : "",
      };

      try {
        const apiCall = grouped ? getGroupedInventoryData : getInventoryData;
        const response = await apiCall({
          page: 1,
          pageSize: pageSize,
          tenant_id: tenantId,
          ...normalizedFilters,
          ...(sortConfig.sortBy && {
            sortBy: sortConfig.sortBy,
            sortOrder: sortConfig.sortOrder || "asc",
          }),
        });

        if (response?.data) {
          if (grouped) {
            // Grouped mode: response.data is array of address groups
            const groups = Array.isArray(response.data) ? response.data : [];

            // Extract grand total MRC from response
            const totalMRC = response.grandTotalMRC || 0;
            setGrandTotalMRC(totalMRC);

            const paginationData = response.pagination || response;
            const responsePageSize =
              paginationData.pageSize || response.pageSize || pageSize;
            const responsePage = paginationData.page || response.page || 1;
            const responseTotal =
              paginationData.total || response.count || response.total || 0;
            const responseTotalPages =
              paginationData.totalPages ||
              Math.ceil(responseTotal / responsePageSize);

            const paginationInfo = {
              page: responsePage,
              total: responseTotal,
              siteCount:
                paginationData.siteCount != null
                  ? paginationData.siteCount
                  : responseTotal,
              totalPages: responseTotalPages,
              pageSize: responsePageSize,
            };

            console.log(
              "📊 Pagination Info (page size change, grouped):",
              paginationInfo
            );
            console.log(
              "📦 Address groups loaded (page size change):",
              groups.length
            );
            console.log("💰 Grand Total MRC (page size change):", totalMRC);

            setInventoryData(groups);
            setPagination((prev) => ({ ...prev, ...paginationInfo }));

            if (
              groups.length > 0 &&
              groups[0].items &&
              groups[0].items.length > 0
            ) {
              const displayColumns = getInventoryDisplayColumns(
                groups[0].items
              );
              setColumns(displayColumns);
            } else {
              setColumns([]);
            }
          } else {
            // Non-grouped mode: response.data is array of individual records
            let records = Array.isArray(response.data) ? response.data : [];

            // Handle API response structure - pagination can be nested or at root
            const paginationData = response.pagination || response;
            const responsePageSize =
              paginationData.pageSize || response.pageSize || pageSize;
            const responsePage = paginationData.page || response.page || 1;
            const responseTotal =
              paginationData.total || response.count || response.total || 0;
            const responseTotalPages =
              paginationData.totalPages ||
              Math.ceil(responseTotal / responsePageSize);

            const paginationInfo = {
              page: responsePage,
              total: responseTotal,
              totalPages: responseTotalPages,
              pageSize: responsePageSize,
            };

            console.log(
              "📊 Pagination Info (page size change):",
              paginationInfo
            );
            console.log(
              "📦 Records loaded (page size change):",
              records.length
            );

            setInventoryData(records);
            setPagination((prev) => ({ ...prev, ...paginationInfo }));

            // Set display columns
            if (records.length > 0) {
              const displayColumns = getInventoryDisplayColumns(records);
              setColumns(displayColumns);
            } else {
              setColumns([]);
            }
          }
        }
      } catch (err) {
        console.error("Failed to load inventory data with new page size:", err);
        
        // Check if it's an authentication error
        if (err.status === 401 || err.isAuthError) {
          console.log("Authentication error detected - stopping requests");
          setHasAuthError(true);
        }
      }
    },
    [
      getInventoryData,
      getGroupedInventoryData,
      grouped,
      filters,
      tenantId,
      sortConfig,
    ]
  );

  const updateSort = useCallback((sortBy, sortOrder) => {
    console.log("🔀 updateSort called:", { sortBy, sortOrder });
    setSortConfig({ sortBy, sortOrder });
    // Reset to page 1 when sort changes (standard UX pattern)
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const refreshData = useCallback(async () => {
    await loadInventoryData(pagination.page);
  }, [loadInventoryData, pagination.page]);

  /** Update grouped row site fields after site details save (no full reload). */
  const patchGroupedSiteStatus = useCallback((addressParts, siteUpdate) => {
    if (!addressParts || !siteUpdate) return;
    const norm = (v) => String(v ?? "").trim().toLowerCase();
    const matchesGroup = (group) =>
      norm(group.std_street_address) === norm(addressParts.std_street_address) &&
      norm(group.std_city) === norm(addressParts.std_city) &&
      norm(group.std_state) === norm(addressParts.std_state) &&
      norm(group.std_zip) === norm(addressParts.std_zip) &&
      norm(group.std_country) === norm(addressParts.std_country);

    setInventoryData((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      let changed = false;
      const next = prev.map((group) => {
        if (!matchesGroup(group)) return group;
        changed = true;
        const site_status = siteUpdate.site_status ?? group.site_status;
        const site_name =
          siteUpdate.site_name !== undefined
            ? siteUpdate.site_name || ""
            : group.site_name;
        const items = Array.isArray(group.items)
          ? group.items.map((item) => ({ ...item, site_status, site_name }))
          : group.items;
        return { ...group, site_status, site_name, items };
      });
      return changed ? next : prev;
    });
  }, []);

  // Load data when filters, sort, or tenant changes
  // Consolidated into single useEffect to prevent cascading triggers
  useEffect(() => {
    // Don't make requests if there's an auth error
    if (hasAuthError) {
      console.log("⛔ Skipping data load due to authentication error");
      return;
    }
    
    if (tenantId) {
      console.log("🔄 Loading data due to dependency change");
      loadInventoryData(1, {}, null, sortConfig);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tenantId,
    JSON.stringify(filters.provider),
    JSON.stringify(filters.category),
    JSON.stringify(filters.serviceType),
    JSON.stringify(filters.billingAccount),
    filters.search,
    JSON.stringify(filters.siteRegion),
    JSON.stringify(filters.siteClassification),
    JSON.stringify(filters.siteGeneralManagerId),
    JSON.stringify(filters.siteSdWanAppliance),
    JSON.stringify(filters.siteStatus),
    JSON.stringify(filters.circuitAction),
    filters.contractEndsWithin,
    sortConfig.sortBy,
    sortConfig.sortOrder,
    hasAuthError,
  ]);

  return {
    inventoryData,
    columns,
    grandTotalMRC,
    pagination,
    filters,
    loading,
    error,
    updateFilters,
    changePage,
    changePageSize,
    refreshData,
    loadInventoryData,
    patchGroupedSiteStatus,
    sortConfig,
    updateSort,
  };
};
